import type { Teammate } from "@repo/app-types/entities/teammate";
import { type FC, useState } from "react";
import { useUpdateMyProfileMutation } from "../../api/entities/teammate.js";
import { signoutUser } from "../../api/supabaseService.js";
import { OnboardingNameForm } from "./onboardingNameForm.js";

interface OnboardingNameFormContainerProps {
  teammate: Teammate;
  /** Called after the profile is saved so the parent can refetch `GET /user`. */
  onSaved: () => void;
}

/**
 * Wires the name-only onboarding form to the profile-update mutation and
 * sign-out. The presentational form lives in {@link ./onboardingNameForm.js}.
 */
export const OnboardingNameFormContainer: FC<
  OnboardingNameFormContainerProps
> = ({ teammate, onSaved }) => {
  const [updateMyProfile, { isLoading }] = useUpdateMyProfileMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onSubmit = (firstName: string, lastName: string) => {
    setErrorMessage(null);
    void (async () => {
      try {
        await updateMyProfile({
          first_name: firstName,
          last_name: lastName,
        }).unwrap();
        onSaved();
      } catch {
        setErrorMessage(
          "Something went wrong saving your details. Please try again.",
        );
      }
    })();
  };

  return (
    <OnboardingNameForm
      email={teammate.email}
      initialFirstName={teammate.first_name || ""}
      initialLastName={teammate.last_name || ""}
      isSaving={isLoading}
      errorMessage={errorMessage}
      onSubmit={onSubmit}
      onSignOut={() => void signoutUser()}
    />
  );
};
